import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { selectUserPreferenceHydration } from '@sophocles/features/userPreferences';
import { bulkUpsertUserPreferences } from '@sophocles/features/userPreferences/api';
import { resolvePreferenceValue } from '@sophocles/features/userPreferences/cache';
import { takePendingPreferenceWrite } from '@sophocles/features/userPreferences/pendingWrite';
import { saveUserPreferenceItems } from '@sophocles/features/userPreferences/save';

/**
 * Persist a user preference to the backend (key-scoped JSON value).
 *
 * On mount, reads the Redux preference cache for `key`. App bootstrap loads
 * all current-user preferences once; this hook consumes that cache and does
 * not issue per-key GET requests. Returns the server value once loaded,
 * falling back to `defaultValue` when no server value exists.
 *
 * On change, debounces a PUT to
 * `PUT /api/odl-app/user-preferences/bulk/` so rapid updates
 * (e.g. toggling columns) collapse into a single write.
 *
 * Also writes to sessionStorage for instant reads on the same tab
 * (avoids a flash of default state on re-render before the API
 * responds).
 *
 * @param {string} key       Preference key, e.g. "grid.siteGrid.ct3.columnVisibility.v2"
 * @param {*}      defaultValue  Fallback while loading / if no server value exists
 * @param {object} [options]
 * @param {number} [options.debounceMs=500]  Write debounce in ms
 */
export default function useUserPreference(key, defaultValue, { debounceMs = 500 } = {}) {
  const dispatch = useDispatch();
  const sessionKey = `pref:${key}`;
  // getPreferenceHydration builds a fresh { record, loaded, loading } object on
  // every call, so useSelector's default reference-equality check would re-render
  // every consumer on every unrelated dispatch. shallowEqual compares the three
  // fields instead, so an unchanged hydration result doesn't re-render.
  const selectHydration = useMemo(() => selectUserPreferenceHydration(key), [key]);
  const hydration = useSelector(selectHydration, shallowEqual);

  // Read from sessionStorage for instant initial value
  const readSession = () => {
    try {
      const raw = sessionStorage.getItem(sessionKey);
      return raw != null ? JSON.parse(raw) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [value, setValueRaw] = useState(readSession);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const pendingValueRef = useRef(null);
  const hasPendingRef = useRef(false);
  const latestValueRef = useRef(value);
  const defaultValueRef = useRef(defaultValue);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    defaultValueRef.current = defaultValue;
  }, [defaultValue]);

  useEffect(() => {
    if (!key) return;
    if (!hydration.record && !hydration.loaded) return;

    const next = resolvePreferenceValue({
      hydration,
      currentValue: latestValueRef.current,
      defaultValue: defaultValueRef.current,
    });
    setValueRaw(next);
    latestValueRef.current = next;

    try {
      if (hydration.record) {
        sessionStorage.setItem(sessionKey, JSON.stringify(next));
      } else {
        sessionStorage.removeItem(sessionKey);
      }
    } catch {
      // ignore
    }
    if (mountedRef.current) setLoaded(true);
  }, [hydration.loaded, hydration.record, key, sessionKey]);

  const persistItems = useCallback(
    items =>
      saveUserPreferenceItems({
        dispatch,
        items,
        persist: bulkUpsertUserPreferences,
        optimistic: false,
      }),
    [dispatch],
  );

  const flushPendingWrite = useCallback(() => {
    const item = takePendingPreferenceWrite({
      key,
      pendingValueRef,
      hasPendingRef,
      timerRef,
    });
    if (!item) return Promise.resolve();
    return persistItems([item]);
  }, [key, persistItems]);

  useEffect(() => {
    if (!key) return undefined;
    return () => {
      flushPendingWrite();
    };
  }, [flushPendingWrite, key]);

  // Debounced write to API
  const persistToApi = useCallback(
    nextValue => {
      if (timerRef.current) clearTimeout(timerRef.current);
      pendingValueRef.current = nextValue;
      hasPendingRef.current = true;
      timerRef.current = setTimeout(() => {
        flushPendingWrite();
      }, debounceMs);
    },
    [debounceMs, flushPendingWrite],
  );

  // Immediately send any pending write, bypassing the debounce.
  // Returns a promise that resolves when the request completes
  // (or resolves immediately if nothing is pending).
  const flush = useCallback(() => {
    return flushPendingWrite();
  }, [flushPendingWrite]);

  const setValue = useCallback(
    updater => {
      const next = typeof updater === 'function' ? updater(latestValueRef.current) : updater;
      latestValueRef.current = next;
      void saveUserPreferenceItems({ dispatch, items: [{ key, value: next }], persist: null });
      persistToApi(next);
      setValueRaw(next);
    },
    [dispatch, key, persistToApi],
  );

  return [value, setValue, loaded, flush];
}
